import Vue from "vue";
import PagoConTarjeta from "@/components/Buy/PagoConTarjeta.vue";

/**
 * Qué hacer con lo que devuelve un punto de venta al empezar un cobro.
 *
 * Hay dos pasarelas y cada una pide algo distinto del navegador:
 *
 *   - Openpay (`modo: 'redirect'`): llevarse al usuario a su página.
 *   - Stripe (`modo: 'formulario'`): pintar el formulario de la tarjeta aquí mismo.
 *
 * Cuál se usa lo decide el administrador desde el CRM, y vale para los dos sistemas
 * a la vez. Esto es el gemelo del `pasarelaPago.js` del CRM, escrito para Vue 2.
 *
 * El formulario se monta sobre la marcha, sin tocar la plantilla de quien llama.
 */

const URL_STRIPE = "https://js.stripe.com/v3/";

let promesaStripe = null;

/**
 * Carga Stripe.js desde su CDN. Tiene que venir de ahí: es lo que mantiene los datos
 * de la tarjeta fuera de nuestro dominio, y lo que Stripe exige.
 */
export function cargarStripe() {
  if (window.Stripe) return Promise.resolve(window.Stripe);
  if (promesaStripe) return promesaStripe;

  promesaStripe = new Promise((resolve, reject) => {
    const existente = document.querySelector('script[src="' + URL_STRIPE + '"]');

    if (existente) {
      existente.addEventListener("load", () => resolve(window.Stripe));
      existente.addEventListener("error", () => reject(new Error("No se pudo cargar Stripe.")));
      return;
    }

    const script = document.createElement("script");
    script.src = URL_STRIPE;
    script.async = true;
    script.onload = () => {
      if (window.Stripe) resolve(window.Stripe);
      else reject(new Error("Stripe se cargó pero no quedó disponible."));
    };
    script.onerror = () => {
      promesaStripe = null;
      reject(new Error("No se pudo cargar Stripe. Revisa tu conexión."));
    };

    document.head.appendChild(script);
  });

  return promesaStripe;
}

/**
 * @param {object} respuesta  lo que devolvió el backend
 * @param {object} opciones
 * @param {(chargeId: string) => Promise<any>} [opciones.confirmar]
 * @param {string} [opciones.titulo]
 * @param {(mensaje: string) => void} [opciones.alFallar]
 */
export function procesarCobro(respuesta, opciones = {}) {
  const datos = (respuesta && respuesta.data && respuesta.data.data)
    || (respuesta && respuesta.data)
    || respuesta
    || {};

  if (datos.modo === "formulario") {
    return abrirFormulario(datos, opciones);
  }

  const url = datos.payment_url || datos.url || datos.redirect_url;

  if (url) {
    window.location.href = url;
    return Promise.resolve({ redirigido: true });
  }

  const mensaje = "La pasarela no devolvió ninguna forma de pagar.";
  if (opciones.alFallar) opciones.alFallar(mensaje);

  return Promise.reject(new Error(mensaje));
}

function abrirFormulario(datos, opciones) {
  if (!datos.client_secret || !datos.publishable_key) {
    const mensaje = "Falta configurar la pasarela de pago. Avisa al administrador.";
    if (opciones.alFallar) opciones.alFallar(mensaje);

    return Promise.reject(new Error(mensaje));
  }

  return new Promise((resolve, reject) => {
    const anfitrion = document.createElement("div");
    document.body.appendChild(anfitrion);

    const Constructor = Vue.extend(PagoConTarjeta);
    const instancia = new Constructor({
      propsData: {
        clientSecret: datos.client_secret,
        publishableKey: datos.publishable_key,
        titulo: opciones.titulo || "Pagar con tarjeta",
        importe: datos.amount != null ? datos.amount : (opciones.importe || null),
        moneda: (datos.currency || "USD").toUpperCase(),
      },
    });

    const quitar = () => {
      instancia.$destroy();
      if (anfitrion.parentNode) anfitrion.parentNode.removeChild(anfitrion);
    };

    instancia.$on("pagado", async (chargeId) => {
      try {
        // El webhook de Stripe hace esto mismo por su cuenta. Se avisa también
        // desde aquí para que el usuario vea el resultado sin esperar, y porque el
        // que llegue segundo no repite nada.
        const resultado = opciones.confirmar
          ? await opciones.confirmar(chargeId || datos.charge_id)
          : null;
        quitar();
        resolve({ pagado: true, chargeId, resultado });
      } catch (e) {
        quitar();
        // El dinero ya salió de la tarjeta: si la confirmación falló, la recoge el
        // webhook. No se le dice al usuario que no pagó, porque pagó.
        e.cobrado = true;
        e.chargeId = chargeId;
        reject(e);
      }
    });

    instancia.$on("cancelado", () => {
      quitar();
      if (opciones.alCancelar) opciones.alCancelar();
      resolve({ cancelado: true });
    });

    instancia.$on("error", (mensaje) => {
      if (opciones.alFallar) opciones.alFallar(mensaje);
    });

    instancia.$mount(anfitrion);
  });
}
